import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { routes } from 'src/router/routes';

import { persistConfig, queryKeys } from '../config';
import {
  activateAccount,
  changePassword,
  forgotPassword,
  login,
  logout,
  register,
  resendActivationLink,
  resetPassword,
} from './methods';

const useLoginMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    mutateAsync: loginMutation,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: login,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [queryKeys.meKey] });
    },
  });

  return { isError, isPending, isSuccess, loginMutation };
};
const useLogoutMutation = () => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: logoutMutation,
  } = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      localStorage.removeItem(persistConfig.key);
      queryClient.removeQueries();
      navigate(routes.login);
    },
  });

  return { isError, isPending, isSuccess, logoutMutation };
};

const useActivateAccountMutation = () => {
  const {
    isPending,
    mutateAsync: activateAccountMutation,
    isError,
    isSuccess,
  } = useMutation({
    mutationFn: activateAccount,
  });

  return { activateAccountMutation, isError, isPending, isSuccess };
};

const useForgotPasswordMutation = () => {
  const {
    isPending,
    mutateAsync: forgotPasswordMutation,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: forgotPassword,
  });

  return { forgotPasswordMutation, isError, isPending, isSuccess };
};

const useRegisterMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    mutateAsync: registerMutation,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: register,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.meKey] });
    },
  });

  return { isError, isPending, isSuccess, registerMutation };
};

const useResendActivationLinkMutation = () => {
  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: resendActivationLinkMutation,
  } = useMutation({
    mutationFn: resendActivationLink,
  });

  return { isError, isPending, isSuccess, resendActivationLinkMutation };
};

const useResetPasswordMutation = () => {
  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: resetPasswordMutation,
  } = useMutation({
    mutationFn: resetPassword,
  });

  return { isError, isPending, isSuccess, resetPasswordMutation };
};

const useChangePasswordMutation = () => {
  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: changePasswordMutation,
  } = useMutation({
    mutationFn: changePassword,
  });

  return { changePasswordMutation, isError, isPending, isSuccess };
};

export {
  useActivateAccountMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useLoginMutation,
  useLogoutMutation,
  useRegisterMutation,
  useResendActivationLinkMutation,
  useResetPasswordMutation,
};
